// src/components/StaffListView.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useOutletContext } from 'react-router-dom';
import neo4jService from '../services/neo4jService'; // Direct import if context not available/reliable here
import { staffQueries } from '../repository/graphQueries';
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip } from 'recharts';
import { formatDate } from '../utils'; // Assuming formatDate is available

// Helper to format data for the small trend charts
const formatTrendData = (weeklyScores) => {
    if (!Array.isArray(weeklyScores) || weeklyScores.length === 0) {
        return [];
    }
    return weeklyScores.map(item => ({
        // Format week for display, or use as is if already formatted
        week: item.week ? (item.week.year ? `${item.week.year}-W${item.week.week}` : formatDate(item.week)) : 'N/A',
        score: item.score
    })).sort((a,b) => new Date(a.week) - new Date(b.week)); // Ensure sorted by week
};


const StaffListView = () => {
    // Try to get neo4jService from context, but have a fallback
    let contextNeo4jService;
    try {
        const context = useOutletContext();
        contextNeo4jService = context?.neo4jService;
    } catch (e) {
        // useOutletContext might not be available if this component is rendered outside an Outlet
        console.warn("useOutletContext not available for StaffListView, falling back to direct import for neo4jService.");
    }
    const activeNeo4jService = contextNeo4jService || neo4jService;


    const [staffData, setStaffData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    const fetchStaffData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            // Ensure Neo4j is initialized (especially if this view can be accessed directly)
            if (!activeNeo4jService.driver) {
                const uri = process.env.REACT_APP_NEO4J_URI || 'neo4j://localhost:7687';
                const user = process.env.REACT_APP_NEO4J_USER || 'neo4j';
                const pass = process.env.REACT_APP_NEO4J_PASSWORD || 'password';
                await activeNeo4jService.initialize(uri, user, pass);
            }

            const result = await activeNeo4jService.runQuery(staffQueries.getStaffPerformanceSummaryList);
            
            setStaffData(result.map(record => ({
                id: record.staffId,
                name: record.staffName,
                imageUrl: record.staffImageUrl, // Use if available
                trailingScore: record.trailingScore,
                weeklyScores: formatTrendData(record.weeklyScores || []), 
            })));
        } catch (err) {
            console.error("Error fetching staff list with performance:", err);
            setError("Failed to load staff data. " + err.message);
        } finally {
            setLoading(false);
        }
    }, [activeNeo4jService]);

    useEffect(() => {
        fetchStaffData();
    }, [fetchStaffData]);

    if (loading) {
        return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-800"></div><span className="ml-3">Loading staff data...</span></div>;
    }

    if (error) {
        return <div className="p-4 bg-red-100 text-red-700 rounded-md"><strong>Error:</strong> {error} <button onClick={fetchStaffData} className="ml-2 bg-red-500 hover:bg-red-700 text-white font-bold py-1 px-2 rounded text-sm">Retry</button></div>;
    }

    if (staffData.length === 0) {
        return <div className="p-4 text-center text-gray-500">No staff members found.</div>;
    }

    return (
        <div className="container mx-auto p-4">
            <h2 className="text-2xl font-semibold text-gray-800 mb-6">Staff Performance Overview</h2>
            <div className="bg-white shadow-md rounded-lg overflow-hidden">
                <table className="min-w-full leading-normal">
                    <thead>
                        <tr className="bg-gray-100 text-left text-gray-600 uppercase text-sm">
                            <th className="px-5 py-3 border-b-2 border-gray-200">Staff Name</th>
                            <th className="px-5 py-3 border-b-2 border-gray-200 text-center">Trailing 2-Wk Score</th>
                            <th className="px-5 py-3 border-b-2 border-gray-200 text-center">Weekly Score Trend (8 wks)</th>
                        </tr>
                    </thead>
                    <tbody className="text-gray-700">
                        {staffData.map((staff) => (
                            <tr key={staff.id} className="border-b border-gray-200 hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/staff/${staff.id}`)}>
                                <td className="px-5 py-4">
                                    <div className="flex items-center">
                                        {staff.imageUrl ? (
                                            <img src={staff.imageUrl} alt={staff.name} className="w-10 h-10 rounded-full mr-3 object-cover" 
                                                 onError={(e) => { e.target.onerror = null; e.target.src = `https://placehold.co/40x40/E2E8F0/718096?text=${staff.name.charAt(0)}`; }} />
                                        ) : (
                                            <div className="w-10 h-10 rounded-full mr-3 bg-gray-200 flex items-center justify-center text-gray-500 font-semibold">
                                                {staff.name ? staff.name.charAt(0) : '?'}
                                            </div>
                                        )}
                                        <Link to={`/staff/${staff.id}`} className="text-blue-600 hover:text-blue-800 font-medium">
                                            {staff.name || 'N/A'}
                                        </Link>
                                    </div>
                                </td>
                                <td className="px-5 py-4 text-center">
                                    <span className={`px-3 py-1 text-sm font-semibold rounded-full ${
                                        staff.trailingScore === null || staff.trailingScore === undefined ? 'bg-gray-200 text-gray-600' :
                                        staff.trailingScore >= 4 ? 'bg-green-100 text-green-700' :
                                        staff.trailingScore >= 3 ? 'bg-yellow-100 text-yellow-700' :
                                        'bg-red-100 text-red-700'
                                    }`}>
                                        {staff.trailingScore !== null && staff.trailingScore !== undefined ? staff.trailingScore.toFixed(2) : 'N/A'}
                                    </span>
                                </td>
                                <td className="px-5 py-4 text-center">
                                    {staff.weeklyScores && staff.weeklyScores.length > 0 ? (
                                        <div style={{ height: '50px', width: '150px', margin: 'auto' }}> {/* Adjust width/height as needed */}
                                            <ResponsiveContainer width="100%" height="100%">
                                                <LineChart data={staff.weeklyScores} margin={{ top: 5, right: 5, left: -30, bottom: 5 }}>
                                                    <XAxis dataKey="week" hide />
                                                    <YAxis domain={[0, 5]} hide />
                                                    <Tooltip 
                                                        contentStyle={{ fontSize: '10px', padding: '2px 5px' }}
                                                        formatter={(value) => [value.toFixed(2), "Score"]}
                                                        labelFormatter={(label) => `Week: ${label}`}
                                                    />
                                                    <Line type="monotone" dataKey="score" stroke="#8884d8" strokeWidth={2} dot={false} />
                                                </LineChart>
                                            </ResponsiveContainer>
                                        </div>
                                    ) : (
                                        <span className="text-xs text-gray-400">No trend data</span>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default StaffListView;